'use client';
import {createStore, type StoreApi} from '@lib/create-store';
import type {IntegrationManifestApiServiceModel, SystemName} from '@linx-security/api-specs';
import {getStorage} from '@stores/storage';
import {createContext, useContext} from 'react';
import {rewrite, pattern} from 'regex';
import {shallow} from 'zustand/shallow';
import {useStoreWithEqualityFn} from 'zustand/traditional';

type IntegrationManifestsStore = {
  manifests: Map<SystemName, IntegrationManifestApiServiceModel>;
  isManifestsLoading: boolean;
  regexMap: Record<string, string>;

  matchIconToApp(app_name: string, fallback: SystemName): SystemName;
  matchIconToApp(app_name: string, fallback?: SystemName): SystemName | null;
  matchIconToApp(app_name: string): SystemName | null;

  setRegexMap: (regexMap: Record<string, string>) => void;
  setManifests: (manifests: Array<IntegrationManifestApiServiceModel>) => void;
  setIsManifestsLoading: (isLoading: boolean) => void;

  getManifest: (system_name: SystemName) => IntegrationManifestApiServiceModel | null | undefined;
};

export type IntegrationManifestsStoreApi = StoreApi<IntegrationManifestsStore>;

const storage = getStorage<IntegrationManifestsStore>();

export const createIntegrationManifestsStore = () => {
  return createStore<IntegrationManifestsStore>(
    (set, get) => {
      const store = {
        regexMap: {},
        matchIconToApp: (app_name: string, fallback?: SystemName) => {
          const {regexMap} = get();
          const matchedAppName =
            Object.keys(regexMap).find(key => {
              try {
                const {expression, flags} = rewrite(regexMap[key]);
                return new RegExp(expression, `${flags}i`).test(app_name);
              } catch (error: unknown) {
                const errorMessage = error instanceof Error ? error.message : String(error);
                console.error(`Invalid regex pattern for key "${key}":`, errorMessage);
                return false;
              }
            }) ?? null;
          return (matchedAppName as SystemName) ?? fallback;
        },
        setRegexMap: (regexMap: Record<string, string>) => {
          const map: Record<string, string> = {};
          // We get the regexMap from the API, in a server flavor, so we need to reverse it.
          Object.entries(regexMap).forEach(([key, value]) => {
            const regex = pattern(value);
            map[key] = regex.toString();
          });
          set({regexMap: map});
        },
        manifests: new Map(),
        setManifests: (manifests: Array<IntegrationManifestApiServiceModel>) => {
          const map = new Map();
          manifests.forEach(manifest => {
            map.set(manifest.system_name, manifest);
          });
          set({manifests: map});
        },
        getManifest: (system_name: SystemName) => {
          if (get().isManifestsLoading) return undefined;
          return get().manifests.get(system_name) ?? null;
        },
        isManifestsLoading: true,
        setIsManifestsLoading: (isLoading: boolean) => {
          set({isManifestsLoading: isLoading});
        },
      };

      return store;
    },
    {
      name: `IntegrationManifestsStore`,
      version: 1,
      persist: {
        storage,
      },
    },
  );
};
export const useIntegrationManifestsStore = <U>(
  selector: (state: IntegrationManifestsStore) => U,
  equalityFn: (left: U, right: U) => boolean = shallow,
): U => {
  const context = useContext(IntegrationManifestsContext);
  if (!context) {
    throw new Error(`useIntegrationManifestsStore must be used within a IntegrationManifestsProvider`);
  }
  return useStoreWithEqualityFn(context, selector, equalityFn);
};

export const IntegrationManifestsContext = createContext<ReturnType<typeof createIntegrationManifestsStore> | null>(
  null,
);
